import React, { useCallback, useEffect, useState } from "react";
import { Button, Chip, Grid, TextField, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { UserRef } from "../../../../models/user";
import { UsersApiService } from "../../../../services/users/users-api.service";
import { UserByEmailFilter } from "../../../../models/user-by-email/user-by-email.filter";
import { UserByEmailDTO } from "../../../../models/user-by-email/user-by-email-dto";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark } from "@fortawesome/free-regular-svg-icons";
import { RemoveButton } from "../styles";
import { faRefresh } from "@fortawesome/free-solid-svg-icons";
import { EMAIL_PATTERN, isValidString } from "../../../../utils/validation/isValidString";

interface Props {
  users: UserRef[];
  externalUsers: string[];
  handleUpdateUsers: (users: UserRef[], externalUsers: string[]) => void;
}

interface State {
  email: string;
  error: string;
  userExists: boolean | null;
  loading: boolean;
}

const PickUsers = ({ handleUpdateUsers, users, externalUsers }: Props) => {
  const theme = useTheme();
  const [state, setState] = useState<State>({
    email: "",
    error: "",
    userExists: null,
    loading: false,
  });

  const checkIfUserExistsInDB = useCallback(async () => {
    if (isValidString(EMAIL_PATTERN, state.email)) {
      setState((state) => ({ ...state, loading: true }));
      try {
        const service = new UsersApiService();
        service.useLoadItemsPath("/GetUserByEmail");

        const response = await service.loadItems({
          email: state.email.trim(),
        } as UserByEmailFilter);

        if (response.error) {
          setState((state) => ({ ...state, error: response.error }));
        } else {
          if (!!(response.items[0] as UserByEmailDTO).user) {
            //means user already in DB
            setState((state) => ({
              ...state,
              error: "",
              userExists: null,
              email: "",
            }));
            handleUpdateUsers(
              [
                ...users.filter((user) => user.id !== (response.items[0] as UserByEmailDTO).user?.id),
                (response.items[0] as UserByEmailDTO).user as UserRef,
              ],
              externalUsers
            );
          } else {
            //user not in DB => update the state to display the confirmation buttons
            setState((state) => ({
              ...state,
              userExists: false,
            }));
          }
        }
      } catch (error) {
        setState((state) => ({ ...state, error: error }));
      } finally {
        setState((state) => ({ ...state, loading: false }));
      }
    }
  }, [state.email, externalUsers, handleUpdateUsers, users]);

  useEffect(() => {
    if (state.email) {
      const checkUserTimeout = setTimeout(checkIfUserExistsInDB, 1200);

      return () => clearTimeout(checkUserTimeout);
    }
  }, [state.email, checkIfUserExistsInDB]);

  return (
    <Grid style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <Grid style={{ display: "flex", alignItems: "center", gap: "15px" }}>
        <TextField
          label="Client email address"
          value={state.email}
          onChange={({ target: { value } }) =>
            setState((state) => ({
              ...state,
              email: value,
            }))
          }
          error={!isValidString(EMAIL_PATTERN, state.email)}
        />
        {state.userExists === false &&
          state.email &&
          isValidString(EMAIL_PATTERN, state.email) &&
          !externalUsers.includes(state.email) && (
            <Grid style={{ display: "flex" }}>
              <Button
                onClick={() => {
                  setState((state) => ({
                    ...state,
                    email: "",
                    userExists: null,
                  }));
                  handleUpdateUsers(users, [
                    ...externalUsers.filter((externalUser) => externalUser !== state.email),
                    state.email,
                  ]);
                }}
                style={{ textTransform: "none" }}
              >
                Send to as external user
              </Button>
            </Grid>
          )}
        {externalUsers.includes(state.email) && (
          <Typography
            variant="body2"
            color={theme.palette.secondary.light}
          >
            User already in the list
          </Typography>
        )}
        {state.loading && (
          <FontAwesomeIcon
            icon={faRefresh}
            spinPulse
            color={theme.palette.primary.main}
          />
        )}
      </Grid>

      <Grid>
        {users.map((user) => (
          <Grid style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <Typography>{user.email}</Typography>
            <Tooltip title="Click to remove this user from the list">
              <RemoveButton
                onClick={() => handleUpdateUsers([...users.filter((_user) => user.id !== _user.id)], externalUsers)}
              >
                <FontAwesomeIcon
                  icon={faCircleXmark}
                  color={theme.palette.error.light}
                />
              </RemoveButton>
            </Tooltip>
          </Grid>
        ))}
        {externalUsers.map((user) => (
          <Grid style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <Typography>{user}</Typography>
            <Chip
              label="External user"
              style={{ color: theme.palette.secondary.light, fontSize: "12px" }}
            />
            <Tooltip title="Click to remove this user from the list">
              <RemoveButton
                onClick={() => handleUpdateUsers(users, [...externalUsers.filter((_user) => user !== _user)])}
              >
                <FontAwesomeIcon
                  icon={faCircleXmark}
                  color={theme.palette.error.light}
                />
              </RemoveButton>
            </Tooltip>
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};

export default PickUsers;
